import { authHeaders } from "./auth";
import type {
  GitConnectionInfo,
  GitProvider,
  FeishuConnectionInfo,
  FeishuScope,
  FeishuRecipientInfo,
} from "@shared/types";

/** REST client for GitHub/GitLab connection management (admin, tenant-scoped). */
async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(e.message ?? e.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface TokenValidation {
  valid: boolean;
  username?: string;
  error?: string;
}

export const integrationsApi = {
  list: async (): Promise<{ connections: GitConnectionInfo[] }> => {
    const res = await fetch("/api/integrations/git", { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { connections: GitConnectionInfo[] };
  },
  validate: (provider: GitProvider, baseUrl: string, token: string): Promise<TokenValidation> =>
    post("/api/integrations/git/validate", { provider, baseUrl, token }),
  discover: (provider: GitProvider, baseUrl: string, token: string): Promise<{ repos: string[] }> =>
    post("/api/integrations/git/discover", { provider, baseUrl, token }),
  create: (body: {
    provider: GitProvider;
    label: string;
    baseUrl: string;
    token: string;
    repo?: string;
    repos?: string[];
  }): Promise<{ connection: GitConnectionInfo }> => post("/api/integrations/git", body),
  remove: async (id: string): Promise<void> => {
    const res = await fetch(`/api/integrations/git/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
};

/** Default base URL shown in the form per provider. */
export function providerDefaultBase(provider: GitProvider): string {
  return provider === "github" ? "https://api.github.com" : "https://gitlab.com";
}

/** A discovered Feishu group the bot can reach. */
export interface FeishuChat {
  chatId: string;
  name: string;
}

/** REST client for Feishu bot connections (admin, tenant-scoped). */
export const feishuApi = {
  list: async (): Promise<{ connections: FeishuConnectionInfo[] }> => {
    const res = await fetch("/api/integrations/feishu", { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { connections: FeishuConnectionInfo[] };
  },
  validate: (appId: string, appSecret: string): Promise<{ valid: boolean; appName?: string; error?: string }> =>
    post("/api/integrations/feishu/validate", { appId, appSecret }),
  discover: (appId: string, appSecret: string): Promise<{ chats: FeishuChat[] }> =>
    post("/api/integrations/feishu/discover", { appId, appSecret }),
  create: (body: {
    label: string;
    appId: string;
    appSecret: string;
    chatId?: string;
    chatName?: string;
    scopes: FeishuScope[];
  }): Promise<{ connection: FeishuConnectionInfo }> => post("/api/integrations/feishu", body),
  remove: async (id: string): Promise<void> => {
    const res = await fetch(`/api/integrations/feishu/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
};

/** A pasted roster row before import (name required, contact fields optional). */
export interface RosterImportRow {
  name: string;
  email?: string;
  mobile?: string;
  role?: string;
}

/** REST client for the Feishu DM recipient roster (admin, tenant-scoped). */
export const feishuRecipientsApi = {
  list: async (): Promise<{ recipients: FeishuRecipientInfo[] }> => {
    const res = await fetch("/api/integrations/feishu/recipients", { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as { recipients: FeishuRecipientInfo[] };
  },
  import: (rows: RosterImportRow[]): Promise<{ recipients: FeishuRecipientInfo[] }> =>
    post("/api/integrations/feishu/recipients/import", { rows }),
  upsert: (body: {
    id?: string;
    name: string;
    role?: string;
    email?: string;
    mobile?: string;
    openId?: string;
    active?: boolean;
  }): Promise<{ recipient: FeishuRecipientInfo }> => post("/api/integrations/feishu/recipients", body),
  resolve: (connectionId: string): Promise<{ updated: number; reason?: string; recipients: FeishuRecipientInfo[] }> =>
    post("/api/integrations/feishu/recipients/resolve", { connectionId }),
  remove: async (id: string): Promise<void> => {
    const res = await fetch(`/api/integrations/feishu/recipients/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
};

/**
 * Split a pasted repo/instance URL into the API base + repo path, so a user who
 * pastes a web URL into the "API 地址" field gets the right base (and a prefilled
 * repo) instead of a 404. Returns null when `raw` is not a URL.
 */
export function splitConnectionUrl(
  provider: GitProvider,
  raw: string,
): { baseUrl: string; repo?: string } | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  const path = u.pathname.replace(/^\/+|\/+$/g, "").replace(/\.git$/, "");
  const repo = /^[^/]+\/[^/]+/.test(path) ? path.split("/").slice(0, 2).join("/") : undefined;
  if (provider === "github") {
    const host = u.hostname.toLowerCase();
    if (host === "github.com" || host === "www.github.com") {
      return { baseUrl: "https://api.github.com", ...(repo !== undefined ? { repo } : {}) };
    }
    if (host === "api.github.com") return { baseUrl: "https://api.github.com" };
    // GitHub Enterprise: the API lives under /api/v3 of the instance origin.
    return { baseUrl: `${u.origin}/api/v3`, ...(repo !== undefined ? { repo } : {}) };
  }
  // GitLab: the base is the instance origin; the path (if any) is the project.
  return { baseUrl: u.origin, ...(repo !== undefined ? { repo } : {}) };
}
